import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {MUTATION_KEYS, QUERY_KEYS} from '@/queryClient'
import {httpGenerateNrAbsences, HttpGenerateNrAbsencesPayload} from '@/features/accesses/http/accesses.http'
import {GenerateNrAbsencesError, GenerateNrAbsencesResponse} from '@/features/accesses/types'
import {invalidateAccessesTableOnly} from '@/features/accesses/cacheUtils'

export const useGenerateNrAbsences = (
    options?: Omit<
        UseMutationOptions<
            GenerateNrAbsencesResponse,
            AxiosError<GenerateNrAbsencesError>,
            HttpGenerateNrAbsencesPayload
        >,
        'mutationKey' | 'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        ...options,
        mutationKey: [MUTATION_KEYS.GENERATE_NR_ABSENCES],
        mutationFn: (payload: HttpGenerateNrAbsencesPayload) => httpGenerateNrAbsences(payload),
        onSuccess: async (data, variables, context) => {
            // A dry run touches nothing, so only a real generation that created something
            // invalidates. The agenda is left alone: its socket `events:created` handler
            // already patches the newly created events into the cache.
            if (!data.dryRun && data.createdAbsences > 0) {
                invalidateAccessesTableOnly(queryClient)
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES_DASHBOARD]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVITY_REPORT_COUNTERS]})
                // The plan is now stale: the freshly created absences count as done treatments.
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.GENERATE_NR_ABSENCES_PREVIEW]})
            }
            await options?.onSuccess?.(data, variables, context)
        }
    })
}
